import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";
import { useBranding } from "@/lib/branding";

const UD_SEAL = "https://customer-assets-wrfwihn1.emergentagent.net/job_alumni-hub-101/artifacts/n52mfin2_image.png";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const b = useBranding();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (e2) {
      setErr(apiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-secondary flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-card rounded-xl shadow-xl border border-border overflow-hidden fade-up">
        <div className="bg-secondary px-6 py-6 flex items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-white flex items-center justify-center p-1">{b.logo_primary ? <img src={b.logo_primary} alt="seal" className="h-full w-full object-contain" /> : null}</div>
          <div className="text-white">
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-accent">{b.university_name}</div>
            <div className="font-serif text-xl">Reset Your Password</div>
          </div>
        </div>
        <div className="p-6">
          {sent ? (
            <div className="text-center py-6" data-testid="forgot-sent">
              <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto" />
              <p className="mt-3 text-sm text-muted-foreground">If an account exists for <b>{email}</b>, we've sent a password reset link. It expires in 1 hour.</p>
              <button data-testid="forgot-back-login" onClick={() => navigate("/login")} className="mt-5 px-5 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90">Back to Sign in</button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="forgot-form">
              <p className="text-sm text-muted-foreground mb-5">Enter your account email and we'll send you a secure link to set a new password.</p>
              {err && <div className="mb-4 text-sm bg-destructive/10 text-destructive rounded-md px-3 py-2" data-testid="forgot-error">{err}</div>}
              <label className="block text-sm font-medium mb-1">Email</label>
              <div className="relative mb-5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input data-testid="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@school.edu" className="w-full pl-9 pr-3 py-2.5 rounded-md border border-input bg-background focus:ring-2 focus:ring-accent focus:outline-none" />
              </div>
              <button data-testid="forgot-submit" type="submit" disabled={busy} className="w-full py-2.5 rounded-md bg-primary text-white font-medium hover:bg-primary/90 transition-colors disabled:opacity-60">{busy ? "Sending…" : "Send reset link"}</button>
              <button type="button" data-testid="forgot-back" onClick={() => navigate("/login")} className="mt-4 w-full flex items-center justify-center gap-1.5 text-sm text-muted-foreground hover:text-primary"><ArrowLeft className="h-4 w-4" /> Back to Sign in</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
